import React, { useEffect, useState } from "react";
import SearchWrapper from "./SearchContainer";
import { debounce } from "lodash";
import SearchParams, { defaultParams } from "../../../types/searchParams";
import SettingsModal from "../../../types/settingsModal";
import { useSelector } from "../../../store";
import { Base } from "../../base";
import ColumnDef from "../../../types/columnDef";
import AttachmentType from "../../../types/attachmentType";
import AddEditAttachmentTypeForm from "../../forms/AddEditAttachmentTypeForm";

interface Props {
  apiAttachmentTypeSearch: (search: SearchParams) => Promise<any>;
  apiGetAttachmentType: (id: number) => void;
  resetAttachmentType: () => void;
  phone: boolean;
  setDeleteModal: React.Dispatch<React.SetStateAction<SettingsModal>>;
  setAttachmentTypeFilter: (params: SearchParams) => void;
  deleteModal: SettingsModal;
}

const AttachmentTypes: React.FC<Props> = (props) => {
  const {
    apiAttachmentTypeSearch,
    apiGetAttachmentType,
    resetAttachmentType,
    phone,
    setDeleteModal,
    setAttachmentTypeFilter,
    deleteModal,
  } = props;
  const { attachmentTypeFilter, attachmentTypeSearchResults, status } =
    useSelector((store) => store.attachmentType);
  const [mobileRows, setMobileRows] = useState<any[]>([]);
  const [addEditModal, setAddEditModal] = useState<boolean>(false);
  const [highlightId, setHighlightId] = useState(0);

  useEffect(() => {
    if (phone) {
      next();
    }
    return () => {
      setAttachmentTypeFilter(defaultParams);
    };
  }, [phone]);

  useEffect(() => {
    if (
      !deleteModal.visible &&
      deleteModal.modal === undefined &&
      highlightId > 0
    ) {
      setHighlightId(0);
    }
  }, [deleteModal.modal, deleteModal.visible, highlightId]);

  const updateSearch = debounce((query: string) => {
    setAttachmentTypeFilter({
      ...attachmentTypeFilter,
      query,
      pageNumber: phone ? 2 : 1,
      pageSize: 10,
    });
    apiAttachmentTypeSearch({
      ...attachmentTypeFilter,
      query,
      pageNumber: 1,
      pageSize: 10,
    }).then((result: any) => {
      setMobileRows(result);
    });
  }, 500);

  const close = () => {
    apiAttachmentTypeSearch(defaultParams);
    setAttachmentTypeFilter(defaultParams);
    setAddEditModal(false);
    resetAttachmentType();
    setHighlightId(0);
  };

  const columns: ColumnDef[] = [
    {
      field: "name",
      headerName: "Name",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      filterable: false,
      renderCell: (params) => <>{params.value}</>,
      mobileSize: 12,
    },
  ];

  const displayRows = (array: any[]) => {
    return array.map((at: AttachmentType) => {
      let rowObject = {
        id: at.id,
        name: at.name,
        actions: (
          <Base.TableActions
            id={at.id}
            handleDelete={() => {
              apiGetAttachmentType(at.id);
              setDeleteModal({ visible: true, modal: "attachmentType" });
              setHighlightId(at.id);
            }}
            handleEdit={() => {
              apiGetAttachmentType(at.id);
              setAddEditModal(true);
              setHighlightId(at.id);
            }}
          />
        ),
      };
      return rowObject;
    });
  };

  const changePage = (pageNumber?: number, pageSize?: number) => {
    apiAttachmentTypeSearch({
      ...attachmentTypeFilter,
      pageNumber,
      pageSize,
    }).then(() => {
      setAttachmentTypeFilter({
        ...attachmentTypeFilter,
        pageNumber,
        pageSize,
      });
    });
  };

  const next = () => {
    apiAttachmentTypeSearch(attachmentTypeFilter).then((result: any) => {
      setAttachmentTypeFilter({
        ...attachmentTypeFilter,
        pageNumber: attachmentTypeFilter?.pageNumber! + 1,
      });
      setMobileRows([...mobileRows, ...result]);
    });
  };

  const handleSaveUpdate = () => {
    apiAttachmentTypeSearch(defaultParams).then((result: any) => {
      setMobileRows(result);
      setAttachmentTypeFilter({ ...defaultParams, pageNumber: 2 });
    });
  };

  return (
    <>
      <SearchWrapper
        updateSearch={updateSearch}
        searchValue={attachmentTypeFilter.query}
        modalOpen={setAddEditModal}
        reset={resetAttachmentType}
      >
        <Base.TableContainer
          columns={columns}
          rows={displayRows(attachmentTypeSearchResults?.results || [])}
          mobileRows={displayRows(mobileRows)}
          page={attachmentTypeFilter.pageNumber}
          pageSize={attachmentTypeFilter.pageSize}
          totalRows={attachmentTypeSearchResults?.totalResults}
          loading={status === "loading"}
          highlightId={highlightId}
          changePage={changePage}
          next={next}
        />
      </SearchWrapper>
      <Base.Modal open={addEditModal} onClose={close}>
        <AddEditAttachmentTypeForm close={close} onSave={handleSaveUpdate} />
      </Base.Modal>
    </>
  );
};

export default AttachmentTypes;
